import { inject, Injectable } from "@angular/core";
import { sift } from "radash";
import {
  catchError,
  defer,
  forkJoin,
  iif,
  map,
  Observable,
  of,
  switchMap
} from "rxjs";
import { AccountBackend } from "src/app/backend/api/account.backend";
import { TenantBackend } from "src/app/backend/api/tenant.backend";
import { UserBackend } from "src/app/backend/api/user.backend";
import { MemberAuth } from "src/app/backend/models/member/member-auth";
import { Account } from "src/app/backend/models/organization/organization";
import { Tenant } from "src/app/backend/models/tenant/tenant";

export type MemberWithAccountAndTenant = {
  member: MemberAuth;
  account: Account;
  tenant: Tenant;
};

@Injectable()
export class MemberService {
  private readonly userBackend = inject(UserBackend);
  private readonly accountBackend = inject(AccountBackend);
  private readonly tenantBackend = inject(TenantBackend);

  listMembersWithAccountAndTenant(): Observable<
    MemberWithAccountAndTenant[] | null
  > {
    return this.userBackend.loadMembers().pipe(
      map((members: MemberAuth[]) =>
        members.filter((member) => !!member.account && !!member.tenant)
      ),
      switchMap((members: MemberAuth[]) => {
        return iif(
          () => members.length > 0,
          defer(() => {
            return forkJoin(
              members.map((memberAuth: MemberAuth) => {
                return forkJoin({
                  account: this.accountBackend
                    .loadPublic(memberAuth.account)
                    .pipe(catchError(() => of(null))),
                  tenant: this.tenantBackend
                    .load(memberAuth.tenant)
                    .pipe(catchError(() => of(null)))
                }).pipe(
                  map(({ account, tenant }) => {
                    if (!account || !tenant) {
                      return null;
                    }

                    return {
                      member: memberAuth,
                      account,
                      tenant
                    };
                  })
                );
              })
            ).pipe(
              map(
                (
                  membersWithAccountAndTenant: Array<MemberWithAccountAndTenant | null>
                ) => sift(membersWithAccountAndTenant)
              )
            );
          }),
          of(null)
        );
      })
    );
  }
}
